import React, {
    PropsWithChildren,
    ReactElement,
    Ref,
    useCallback, useEffect,
    useRef,
    useState
} from 'react';
import {FormState, useFormContext} from "react-hook-form";
import {InputMaskProps} from "@react-input/mask";
import SimpleSelectInput from "@/app/components/atomic/Input/SimpleSelectInput";
import AppTooltip from "@/app/components/atomic/molecules/AppTooltip";
import dynamic from "next/dynamic";

const TextInput = dynamic(() => import('./TextInput'))
const NumericInput = dynamic(() => import('./NumericInput'))
const DateInput = dynamic(() => import('@/app/components/atomic/Input/DateInput'))
const SelectInput = dynamic(() => import('@/app/components/atomic/Input/SelectInput'))

export interface GenericInputReturn<T = string> {
    value: T;
    fieldName: string;
}

export interface GenericInputProps<T = string> {
    fieldName: string;
    onChange: (inputReturn: GenericInputReturn) => void;
    onClick?: () => void;
    inputProps?: any;
    className?: string;
    value: string | number | undefined | null;
    borders?: keyof typeof BORDERS;
}

interface AppInputProps extends GenericInputProps {
    id?: string;
    type?: any;
    min?: number;
    max?: number;
    addonContent?: string | ReactElement;
    prefixContent?: AppInputProps["addonContent"];
    addonTheme?: IAddonContentProps["theme"];
    required?: boolean;
    debounce?: number | null;
    placeholder?: string;
    mask?: InputMaskProps["mask"];
    replacement?: InputMaskProps["replacement"];
    track?: InputMaskProps["track"];
    validation?: boolean;
    ref?: Ref<any>;
    disabled?: boolean;
    autoComplete?: string;
    wrapperClass?: string;
    placement?: 'top' | 'bottom';
    isSearchable?: boolean;
}

export const TYPE_TEXT = 'text';
export const TYPE_TEXT_NUMBER = 'text/number';

export const INPUT_TYPE_NUMBER = 'number';
export const INPUT_TYPE_DATE = 'date';
export const INPUT_TYPE_SELECT = 'select';
export const INPUT_TYPE_SIMPLE_SELECT = 'simple-select';
const TYPE_COMPONONENT_MAP: { [key: string]: any } = {
    [TYPE_TEXT]: TextInput,
    [INPUT_TYPE_NUMBER]: NumericInput,
    [INPUT_TYPE_DATE]: DateInput,
    [INPUT_TYPE_SELECT]: SelectInput,
    [INPUT_TYPE_SIMPLE_SELECT]: SimpleSelectInput,
} as const

const BORDERS = {
    default: 'border border-blue-300 hover:border-blue-500 focus-within:border-blue-500',
    neutral: 'border border-rm-gray-light hover:border-rm-gray focus-within:border-rm-gray',
} as const;

const DEFAULT_BORDERS = 'default';

function AppInput({
                      id,
                      value,
                      onChange,
                      onClick,
                      fieldName,
                      addonContent,
                      prefixContent,
                      addonTheme,
                      required,
                      min,
                      max,
                      debounce,
                      placeholder,
                      validation = undefined,
                      type = TYPE_TEXT,
                      ref,
                      mask,
                      replacement,
                      track,
                      inputProps = {},
                      className,
                      wrapperClass,
                      placement,
                      borders = DEFAULT_BORDERS,
                      disabled = false,
                      autoComplete,
                      isSearchable = false,
                  }: AppInputProps) {
    const bordersTheme = BORDERS[borders];

    const inputRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const {
        formState = {},
        setValue = () => {
        },
    }: { formState: FormState<any> | {}, setValue: Function } = useFormContext() || {}

    useEffect(() => {
        setValue(fieldName, value)
    }, []);

    const onChangeWithValidation = (inputReturn: GenericInputReturn) => {
        setValue(fieldName, (inputReturn as any).value)
        onChange(inputReturn)
    }
    const renderInput = () => {

        if (Object.keys(TYPE_COMPONONENT_MAP).includes(type)) {
            const InputComponent = TYPE_COMPONONENT_MAP[type]
            return (
                <InputComponent
                    id={id}
                    value={value || ""}
                    inputRef={inputRef}
                    onChange={onChangeWithValidation}
                    onClick={onClick}
                    fieldName={fieldName}
                    required={required}
                    debounce={debounce}
                    placeholder={placeholder}
                    validation={validation}
                    inputProps={inputProps}
                    mask={mask}
                    replacement={replacement}
                    track={track}
                    disabled={disabled}
                    autoComplete={autoComplete}
                    placement={placement}
                    isSearchable={isSearchable}
                    className={`${prefixContent ? 'pl-0' : ''} ${className}`}
                />
            );
        } else {
            return (
                <NumericInput
                    value={value}
                    onChange={onChangeWithValidation}
                    onClick={onClick}
                    fieldName={fieldName}
                    required={required}
                    min={min}
                    max={max}
                    debounce={debounce}
                />
            );
        }
    };

    const getError = useCallback(() => {
        const fieldErrors = (formState as FormState<any>)?.errors?.[fieldName as string]
        return fieldErrors
    }, [(formState as FormState<any>)?.errors])

    return (
        <div
            onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
            className={`flex relative items-center rounded w-full min-h-12 transition-all duration-500
         ${getError() ? 'border-red-500' : ''}
         ${disabled ? 'border !bg-gray-100 !border-gray-200 hover:border-gray-200 !text-gray-600' : bordersTheme + ' bg-white'} 
         ${wrapperClass}`}
        >
            {prefixContent && (
                <AddonContent theme={addonTheme}>
                    {prefixContent}
                </AddonContent>
            )}
            <AppTooltip
                isOpen={!!(isFocused && getError())}
                label={getError()?.message as string}
            >
                {renderInput()}
            </AppTooltip>
            {addonContent && (
                <AddonContent theme={addonTheme} place={"right"}>
                    {addonContent}
                </AddonContent>
            )}
        </div>
    );
}

interface IAddonContentProps extends PropsWithChildren {
    place?: 'left' | 'right';
    theme?: keyof typeof ADDON_CONTENT_THEMES;
}

const ADDON_CONTENT_THEMES = {
    accent: 'bg-blue-100 border-t border-b border-inherit',
    blank: 'bg-none border-none',
}

const AddonContent = (props: IAddonContentProps) => {
    const {theme = 'accent'} = props
    return <div
        className={`flex items-center justify-center h-12 px-4 text-md font-semibold
        ${props.place === 'right' ? 'border-l' : 'border-r'} ${ADDON_CONTENT_THEMES[theme]}
        `}
    >
        {props.children}
    </div>
}

export default React.memo(AppInput)
